import { PayoutStatus } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import type { PayoutRescueTranslations } from "@/shared/lang/interfaces/payoutRescue";
import { formatRail } from "@/shared/utils/railDisplay";

/**
 * Backend pagination cap (`PaginationDto.Max(200)`). The pending/processing queues fetch ONE page at this cap
 * and sort/filter client-side — they are exception queues (normally near-empty); if `total` ever exceeds it
 * the UI discloses the truncation instead of silently capping.
 */
export const QUEUE_FETCH_LIMIT = 200;

/** History tab: server-side offset pagination. */
export const HISTORY_PAGE_SIZE = 25;

/** Age SLA thresholds for a stuck-CREATED payout (design brief: >1h amber, >4h red). */
export const AGE_AMBER_MINUTES = 60;
export const AGE_RED_MINUTES = 240;

/** Cancel reason max length — mirrors coloca-nest `CancelPayoutDto` (`@MaxLength(200)`). */
export const CANCEL_REASON_MAX_LENGTH = 200;

/**
 * Retry confirm arming delay: the primary button stays disabled while the operator is expected to read the
 * money-movement summary (trust-architecture friction, not a spinner).
 */
export const RETRY_ARM_DELAY_MS = 1500;

/** ColocaStatusPill variant per raw payout status (CANCELLED is a deliberate action — neutral, never red). */
export const RESCUE_PILL_VARIANT: Record<
  PayoutStatus,
  "success" | "info" | "warning" | "danger" | "neutral"
> = {
  [PayoutStatus.CREATED]: "warning",
  [PayoutStatus.PROCESSING]: "info",
  [PayoutStatus.PAID]: "success",
  [PayoutStatus.FAILED]: "danger",
  [PayoutStatus.CANCELLED]: "neutral",
  [PayoutStatus.REVERSED]: "danger",
};

/**
 * Raw status → i18n label key (`payoutRescue` namespace). In this surface CREATED reads as "Por rescatar" (a
 * payout RESTING in CREATED is an anomaly — auto-dispatch runs inside the create request). Unknown new
 * statuses fall back to the raw enum value at the call sites.
 */
export const RESCUE_STATUS_LABEL_KEYS: Partial<Record<string, keyof PayoutRescueTranslations>> = {
  [PayoutStatus.CREATED]: "status_created",
  [PayoutStatus.PROCESSING]: "status_processing",
  [PayoutStatus.PAID]: "status_paid",
  [PayoutStatus.FAILED]: "status_failed",
  [PayoutStatus.CANCELLED]: "status_cancelled",
  [PayoutStatus.REVERSED]: "status_reversed",
};

/** Long-form status descriptions for the drawer header chip. */
export const RESCUE_STATUS_LONG_KEYS: Partial<Record<string, keyof PayoutRescueTranslations>> = {
  [PayoutStatus.CREATED]: "status_long_created",
  [PayoutStatus.PROCESSING]: "status_long_processing",
  [PayoutStatus.PAID]: "status_long_paid",
  [PayoutStatus.FAILED]: "status_long_failed",
  [PayoutStatus.CANCELLED]: "status_long_cancelled",
  [PayoutStatus.REVERSED]: "status_long_reversed",
};

/**
 * Corridors live in runtime v1 — the country implies rail + currency (BRA→PIX/BRL, MEX→SPEI/MXN,
 * ARG→CBU-CVU/ARS), so the filter sends only `country` and stays correct if new rails land later. `rail` is
 * the display label only; where the wire enum differs from the label (ARG `CBU_CVU`) it derives from
 * `formatRail` so the mapping has a single source of truth.
 */
export interface RescueCorridorOption {
  country: string;
  currency: string;
  rail: string;
}
export const RESCUE_CORRIDOR_OPTIONS: readonly RescueCorridorOption[] = [
  { country: "MEX", currency: "MXN", rail: "SPEI" },
  { country: "BRA", currency: "BRL", rail: "PIX" },
  { country: "ARG", currency: "ARS", rail: formatRail("CBU_CVU") },
  { country: "COL", currency: "COP", rail: formatRail("BREB") },
];

/** History status filter — single-select (the API takes ONE enum value; no multi). */
export const HISTORY_STATUS_OPTIONS: readonly PayoutStatus[] = Object.values(PayoutStatus);
